<script>
/**
 * DoughnutChart creates a doughnut visualization based on the predefined 
 * configuration definition and the data provided as 'stats' prop. Reactive 
 * to data changes.
 */

import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Doughnut, mixins } from 'vue-chartjs';
import Chart from 'chart.js';

Chart.plugins.unregister(ChartDataLabels);

export default {
    extends: Doughnut,
    name: 'DoughnutChart',
    mixins: [ mixins.reactiveData ],
    props: {
        stats: {
            type: Object,
            required: true
        },
        countStats: {
          type: Object,
          required: false
        },
        mobile: {
            type: Boolean,
            default: false
        },
        showTotal: {
            type: Boolean,
            default: true
        },
        defaultTotal: {
            type: Number,
            default: null
        },
        showLabels: {
            type: Boolean,
            default: false
        },
        showTooltips: {
            type: Boolean,
            default: false
        },
        showCountAndPercentage: {
            type: Boolean,
            default: false
        },
        unit: {
            type: String,
            default: ''
        }
    },
    data: () => ({
        total: 0,
        hidden: []
    }),
    mounted() {
        this.renderChart(this.stats, this.options());
        this.addPlugin(this.marginTopPlugin());
        if (this.showTotal) this.addPlugin(this.centerTextPlugin());
        if (this.showLabels) this.addPlugin(ChartDataLabels);
    },
    updated() {
        this.renderChart(this.stats, this.options());
    },
    methods: {
        options() {
            const colors = this.$vuetify.theme.isDark ?
                this.$vuetify.theme.themes.dark : this.$vuetify.theme.themes.light;

            const options = {
                responsive: true,
                maintainAspectRatio: false,
                borderWidth: 0,
                legend: {
                    height: 60,
                    labels: {
                        fontColor: colors.ddwhite,
                        fontSize: 14
                    }
                },
                tooltips: this.showTooltips ? {
                    callbacks: {
                        label: (item) => {
                          const value = this.stats.datasets[0].data[item.index];
                          const label = this.stats.labels[item.index];
                          return `${label}: ${value}${this.unit}`;
                        },
                    },
                } : false,
                layout: {
                    padding: {
                        top: 0,
                        right: this.mobile ? 0 : 60,
                        left: this.mobile ? 0 : 60,
                        bottom: 0
                    }
                },
                plugins: {
                    datalabels: {
                        color: colors.ddwhite,
                        anchor: 'center',
                        align: 'center',
                        textAlign: 'center',
                        font: {
                            size: this.mobile ? 12 : 16,
                            weight: 'bold'
                        },
                        formatter: (value, context) => {
                            if (Number(value)) {
                                const { dataIndex } = context;
                                const label = this.stats.labels[dataIndex];
                                const printValue = this.countStats && this.countStats[label] ?
                                    `${this.countStats[label]} (${value})` : value;
                                return `${label}\n${printValue}${this.unit}`;
                            }
                            return '';
                        }
                    }
                },
                elements: {}
            };
            
            if (this.showTotal) {
                options.elements = {
                    center: { 
                        text: this.defaultTotal !== null ? new Intl.NumberFormat("US").format(this.defaultTotal) : this.calcTotal(),
                        color: "black"
                    }
                };
            }
            return options;
        },
        calcTotal() {
            if (
                this.stats["datasets"] === undefined ||
                this.stats.datasets.length === 0 ||
                this.stats.datasets[0]["data"] === undefined ||
                this.stats.datasets[0].data.length === 0
            ) return "-";

            return this.stats.datasets[0].data.reduce((t, n) => t + n); 
        },
        marginTopPlugin() {
            return {
                id: 'marginTop',
                beforeInit: function(chart) {
                    const newLegend = new Chart.Legend({
                        ctx: chart.chart.ctx,
                        options: chart.options.legend,
                        chart,
                        afterFit: function() {
                            this.height = this.height + 50;
                        }
                    });
                    Chart.layoutService.removeBox(chart, chart.legend);
                    Chart.layoutService.addBox(chart, newLegend);
                }
            }
        },
        centerTextPlugin() {
            return {
                id: 'centerTotal',
                beforeDraw: function(chart) {
                    if (chart.config.options.elements.center) {
                        //Get ctx from string
                        var ctx = chart.chart.ctx;
                        //Get options from the center object in options
                        var centerConfig = chart.config.options.elements.center;
                        var fontStyle = centerConfig.fontStyle || 'Arial';
                        var txt = centerConfig.text;
                        var color = '#000';
                        var sidePadding = centerConfig.sidePadding || 20;
                        var sidePaddingCalculated = (sidePadding/100) * (chart.innerRadius * 2)
                        //Start with a base font of 30px
                        ctx.font = '30px ' + fontStyle;

                        //Get the width of the string and also the width of the element minus 10 to give it 5px side padding
                        var stringWidth = ctx.measureText(txt).width;
                        var elementWidth = (chart.innerRadius * 2) - sidePaddingCalculated;

                        // Find out how much the font can grow in width.
                        var widthRatio = elementWidth / stringWidth;
                        var newFontSize = Math.floor(30 * widthRatio) - 8;
                        var elementHeight = (chart.innerRadius * 2);

                        // Pick a new font size so it will not be larger than the height of label.
                        var fontSizeToUse = Math.min(newFontSize, elementHeight);

                        //Set font settings to draw it correctly.
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';
                        var centerX = ((chart.chartArea.left + chart.chartArea.right) / 2);
                        var centerY = ((chart.chartArea.top + chart.chartArea.bottom) / 2) + 5;
                        ctx.font = fontSizeToUse + 'px ' + fontStyle;
                        ctx.fillStyle = color;

                        //Draw text in center
                        ctx.fillText("", centerX, centerY);
                        ctx.clearRect (centerX - stringWidth / 2, centerY - fontSizeToUse / 2, stringWidth, fontSizeToUse);

                        ctx.fillText(txt, centerX, centerY);
                    }
                }
            }
        }
    },
    watch: {
        stats(newStats) {
            this.renderChart(newStats, this.options());
        },
        total() {
            this.renderChart(this.stats, this.options());
        },
        mobile() {
            this.renderChart(this.stats, this.options());
        }
    }
}
</script>
